/*
  BlockDetailPane — view + edit one Program Block inside the Program
  Library (same master-detail feel as Templates).

  Save rules (backend PUT /program-blocks/{id}/content):
    * Blocks never sit as drafts — every save is published.
    * Published → saved straight away as a NEW published version;
      earlier versions stay in history. Programs that already inserted
      the block keep their own copy.
    * Archived → read-only until restored.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Save, Archive, RotateCcw, Trash2, CopyPlus, History, Star,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { UsageChip } from "@/components/SectionCategoriesCard";
import { SectionEditor, DEFAULT_SECTION_TYPES } from "@/components/TemplateManageDialog";

export const STATUS_TONE = {
  draft: "bg-blue-50 text-blue-800 border-blue-300",
  published: "bg-emerald-50 text-emerald-800 border-emerald-300",
  archived: "bg-zinc-100 text-zinc-500 border-zinc-300",
};

const EMPTY_SECTION = { key: "warmup", label: "Warm-up", narrative: "", exercises: [] };

// Older blocks were written by the retired single-section editor with
// `weight` / `notes`; the shared exercise editor uses `load` / `instructions`.
function normaliseExercise(ex) {
  const out = { ...ex };
  if (out.weight && !out.load) out.load = out.weight;
  if (out.notes && !out.instructions) out.instructions = out.notes;
  delete out.weight;
  delete out.notes;
  return out;
}

function useSectionTypes() {
  const [types, setTypes] = useState(DEFAULT_SECTION_TYPES);
  useEffect(() => {
    api.get("/exercise-programs/section-categories")
      .then((r) => {
        const items = (r.data?.items || []).map((i) => ({ key: i.key, label: i.label }));
        if (items.length) setTypes(items);
      })
      .catch(() => {});
  }, []);
  return types;
}

function useSectionMutators(setSection) {
  return useMemo(() => {
    const mutEx = (fn) => setSection((s) => ({ ...s, exercises: fn([...(s.exercises || [])]) }));
    const mutAlts = (ei, fn) => mutEx((xs) => {
      xs[ei] = { ...xs[ei], alternatives: fn([...(xs[ei].alternatives || [])]) };
      return xs;
    });
    return {
      patch: (ch) => setSection((s) => ({ ...s, ...ch })),
      addExercise: () => mutEx((xs) => [...xs, { name: "New exercise" }]),
      patchExercise: (ei, ch) => mutEx((xs) => { xs[ei] = { ...xs[ei], ...ch }; return xs; }),
      removeExercise: (ei) => mutEx((xs) => xs.filter((_, i) => i !== ei)),
      moveExercise: (ei, d) => mutEx((xs) => {
        const j = ei + d;
        if (j < 0 || j >= xs.length) return xs;
        [xs[ei], xs[j]] = [xs[j], xs[ei]];
        return xs;
      }),
      addAlt: (ei) => mutAlts(ei, (a) => [...a, { name: "New alternative" }]),
      patchAlt: (ei, ai, ch) => mutAlts(ei, (a) => { a[ai] = { ...a[ai], ...ch }; return a; }),
      removeAlt: (ei, ai) => mutAlts(ei, (a) => a.filter((_, i) => i !== ai)),
    };
  }, [setSection]);
}

function VersionHistory({ versions }) {
  const [open, setOpen] = useState(false);
  if (!versions?.length) return null;
  return (
    <div className="border-t border-zinc-100 pt-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="text-2xs uppercase tracking-wide text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1"
              data-testid="block-versions-toggle">
        <History className="h-3 w-3" /> Version history ({versions.length})
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1" data-testid="block-versions-list">
          {versions.map((v) => (
            <li key={v.id} className="text-xs text-zinc-700 flex items-center gap-2">
              <span className="font-medium tabular-nums">v{v.version_number}</span>
              <Badge className={`text-2xs uppercase border ${STATUS_TONE[v.status] || ""}`}>{v.status}</Badge>
              <span className="text-zinc-500">
                {new Date(v.published_at || v.created_at).toLocaleDateString()}
              </span>
              <span className="truncate text-zinc-600">{v.change_summary}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function BlockDetailPane({
  blockId, canEdit, onChanged, onDuplicated, onDeleted, onOpenUsage, onDirtyChange,
}) {
  const [block, setBlock] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", service: "", tags: "" });
  const [section, setSection] = useState(EMPTY_SECTION);
  const [baseline, setBaseline] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const sectionTypes = useSectionTypes();
  const m = useSectionMutators(setSection);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/program-blocks/${blockId}`);
      const b = r.data;
      const vid = b.draft_version_id || b.current_version_id;
      let sect = EMPTY_SECTION;
      if (vid) {
        const v = await api.get(`/program-blocks/${blockId}/versions/${vid}`);
        sect = (v.data?.content?.sections || [])[0] || EMPTY_SECTION;
      }
      sect = { ...sect, exercises: (sect.exercises || []).map(normaliseExercise) };
      const f = { name: b.name || "", description: b.description || "",
                  service: b.service || "", tags: (b.tags || []).join(", ") };
      setBlock(b);
      setForm(f);
      setSection(sect);
      setBaseline(JSON.stringify({ f, sect }));
    } catch (e) {
      toast.error("Couldn't load block", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [blockId]);

  useEffect(() => { load(); }, [load]);

  const metaDirty = block && JSON.stringify(form) !== JSON.stringify(JSON.parse(baseline || "{}").f || {});
  const contentDirty = block && JSON.stringify(section) !== JSON.stringify(JSON.parse(baseline || "{}").sect || {});
  const dirty = !!(metaDirty || contentDirty);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const archived = block?.status === "archived";
  const editable = canEdit && !archived;

  async function run(fn, okMsg) {
    setBusy(true);
    try {
      const msg = await fn();
      if (msg !== false) toast.success(msg || okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  const save = () => run(async () => {
    if (!form.name.trim()) throw new Error("Name can't be empty");
    if (metaDirty) {
      await api.post(`/program-blocks/${blockId}`, {
        name: form.name.trim(), description: form.description.trim(), service: form.service.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
    }
    if (contentDirty) {
      const r = await api.put(`/program-blocks/${blockId}/content`, { section });
      if (r.data?.created_version) {
        return `Saved as version ${r.data.version.version_number} — now live in the library`;
      }
    }
    return "Saved";
  });

  const archive = () => {
    const usage = block?.usage_count || 0;
    if (!window.confirm(`Archive "${block.name}"?${usage ? ` ${usage} program(s) keep their inserted copy.` : ""} It will be hidden from the Insert-from-library picker.`)) return;
    run(() => api.post(`/program-blocks/${blockId}/archive`, {}), "Archived");
  };
  const restore = () => run(() => api.post(`/program-blocks/${blockId}/restore`, {}), "Restored");
  async function toggleFav() {
    const next = !block.is_favourite;
    setBlock((b) => ({ ...b, is_favourite: next }));
    try {
      await api.post(`/program-blocks/${blockId}/favourite`, { favourite: next });
      onChanged?.();
    } catch (e) {
      setBlock((b) => ({ ...b, is_favourite: !next }));
      toast.error("Couldn't update favourite", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function duplicate() {
    setBusy(true);
    try {
      const r = await api.post(`/program-blocks/${blockId}/duplicate`, {});
      toast.success(`Duplicated "${block.name}"`);
      onDuplicated?.(r.data?.block?.id);
    } catch (e) {
      toast.error("Duplicate failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function hardDelete() {
    if (!window.confirm(`Permanently delete "${block.name}"? This removes every version and cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/program-blocks/${blockId}`);
      toast.success("Block deleted");
      onDeleted?.();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  if (loading || !block) {
    return (
      <div className="text-sm text-zinc-500 py-10 text-center" data-testid="block-detail-loading">
        <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading block…
      </div>
    );
  }

  const latestVersion = (block.versions || [])[0]?.version_number;
  return (
    <div className="flex flex-col gap-3" data-testid="block-detail-pane">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={toggleFav} disabled={busy}
                className="p-0.5 rounded hover:bg-zinc-100"
                title={block.is_favourite ? "Remove from favourites" : "Add to favourites"}
                data-testid="block-detail-fav">
          <Star className={`h-4 w-4 ${block.is_favourite ? "fill-amber-400 text-amber-500" : "text-zinc-400"}`} />
        </button>
        <div className="text-base font-semibold text-zinc-950 truncate" data-testid="block-detail-name">{block.name}</div>
        <Badge className={`text-2xs uppercase border ${STATUS_TONE[block.status]}`} data-testid="block-detail-status">
          {block.status}{latestVersion ? ` · v${latestVersion}` : ""}
        </Badge>
        <UsageChip count={block.usage_count || 0} testId="block-detail-usage"
                   onClick={block.usage_count ? () => onOpenUsage?.(blockId) : undefined} />
        {dirty && <span className="text-2xs text-amber-700" data-testid="block-detail-unsaved">Unsaved changes</span>}
      </div>

      {archived && (
        <div className="text-xs rounded border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-zinc-700">
          Archived — restore this block to edit it.
        </div>
      )}
      {editable && block.status === "published" && (
        <div className="text-xs rounded border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 text-emerald-900"
             data-testid="block-detail-published-note">
          Saving exercise changes publishes a new version straight away. Programs that already use this block keep their copy.
        </div>
      )}

      <fieldset disabled={!editable || busy} className="space-y-3 disabled:opacity-90">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="text-2xs uppercase text-zinc-500">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="block-detail-name-input" />
          </div>
          <div>
            <label className="text-2xs uppercase text-zinc-500">Service</label>
            <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
                   placeholder="pilates / onero / …" data-testid="block-detail-service" />
          </div>
        </div>
        <div>
          <label className="text-2xs uppercase text-zinc-500">Description</label>
          <Textarea rows={2} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="block-detail-description" />
        </div>
        <div>
          <label className="text-2xs uppercase text-zinc-500">Tags (comma-separated)</label>
          <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                 data-testid="block-detail-tags" />
        </div>
        <div>
          <div className="text-2xs uppercase text-zinc-500 mb-1">Section & exercises</div>
          <SectionEditor
            section={section} index={0} allSections={[section]} sectionTypes={sectionTypes} singleSection
            onPatch={m.patch}
            onAddExercise={m.addExercise}
            onPatchExercise={m.patchExercise}
            onRemoveExercise={m.removeExercise}
            onMoveExercise={m.moveExercise}
            onAddAlt={m.addAlt}
            onPatchAlt={m.patchAlt}
            onRemoveAlt={m.removeAlt}
          />
        </div>
      </fieldset>

      <VersionHistory versions={block.versions} />

      {canEdit && (
        <div className="flex items-center gap-2 flex-wrap border-t pt-3">
          <Button variant="outline" onClick={duplicate} disabled={busy} data-testid="block-detail-duplicate">
            <CopyPlus className="h-3.5 w-3.5 mr-1" /> Duplicate as new
          </Button>
          {!archived ? (
            <Button variant="ghost" onClick={archive} disabled={busy} data-testid="block-detail-archive">
              <Archive className="h-3.5 w-3.5 mr-1" /> Archive
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={restore} disabled={busy} data-testid="block-detail-restore">
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
              </Button>
              {(block.usage_count || 0) === 0 && (
                <Button variant="ghost" onClick={hardDelete} disabled={busy}
                        className="text-red-700 hover:bg-red-50" data-testid="block-detail-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              )}
            </>
          )}
          <div className="flex-1" />
          {!archived && (
            <Button onClick={save} disabled={busy || !dirty} data-testid="block-detail-save">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Save changes
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
